const SYS_DIAG_JUMP_QUEUE_KEY = "sysDiagJumpQueue";

/**
 * One hand-off from Unit Diagnostics to System Diagnostics, passed through
 * localStorage because the jump opens a new tab (CM-325).
 *
 * Only `systemId` is required — everything else is whatever context the
 * originating screen happened to have. A `unitType` outside the three values
 * below (a service unit, for instance) has no list to be placed in on the
 * System Diagnostics side, so those jumps should omit the unit fields entirely
 * rather than send a type the reader will silently ignore.
 */
export interface ISysDiagJumpContext {
  systemId: string;
  /** Calendar day to preselect, `YYYY-MM-DD`. Derive it in the *site's* timezone. */
  day?: string;
  /** Epoch ms of the exact point to place the time slider on. */
  timestamp?: number;
  unitType?: "indoor" | "outdoor" | "bsBox";
  unitId?: string;
  /** Parameter codes to preselect in the table. */
  params?: string[];
}

const readQueue = (): ISysDiagJumpContext[] => {
  try {
    const queue = JSON.parse(localStorage.getItem(SYS_DIAG_JUMP_QUEUE_KEY) || "[]");
    return Array.isArray(queue) ? queue : [];
  } catch {
    return [];
  }
};

/**
 * Queues a jump for System Diagnostics to pick up on its next load.
 *
 * Appends rather than overwrites: two jumps fired in quick succession — the
 * user opening two different systems within a second or two — each keep their
 * own context instead of the second clobbering the first.
 */
export const queueSysDiagJump = (context: ISysDiagJumpContext): void => {
  localStorage.setItem(SYS_DIAG_JUMP_QUEUE_KEY, JSON.stringify([...readQueue(), context]));
};

/**
 * Consumes the entry (if any) left for this system, removing only the matching
 * entries so concurrent jumps to other systems aren't clobbered.
 */
export const consumeSysDiagJumpContext = (
  systemId?: string
): ISysDiagJumpContext | null => {
  if (!systemId) {
    return null;
  }
  const queue = readQueue();
  if (!queue.length) {
    return null;
  }
  const matching = queue.filter((entry) => entry?.systemId === systemId);
  const remaining = queue.filter((entry) => entry?.systemId !== systemId);
  if (remaining.length !== queue.length) {
    if (remaining.length) {
      localStorage.setItem(SYS_DIAG_JUMP_QUEUE_KEY, JSON.stringify(remaining));
    } else {
      localStorage.removeItem(SYS_DIAG_JUMP_QUEUE_KEY);
    }
  }
  return matching.length ? matching[matching.length - 1] : null;
};
